import { FormattedMessage, useIntl } from 'react-intl';
import { FilterItem } from '@molecules/filters/filtersMenu';
import { PickersActionBarProps, StaticDateRangePicker } from '@atoms/inputs';
import { Button } from '@atoms/buttons';
import { Flex } from '@atoms/layout';
import {  Filter } from '@libs/store/filters';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { DateRangeShortcutsItem } from '@libs/models/common';
const FallbackTimezone = TIMEZONE_FALLBACK;

type DateRangeFilterMenuProps = {
  localizationSubset: string;
  dateRangeShortcuts: DateRangeShortcutsItem[];
  setFilter: (filter: Filter) => void;
  clearFilter: (key: string) => void;
  key: string;
  timezone: string;
};
export function useDateRangeFilterMenu({
  dateRangeShortcuts,
  setFilter,
  clearFilter,
  localizationSubset,
  key: filterKey,
  timezone = FallbackTimezone,
}: DateRangeFilterMenuProps): FilterItem {
  const intl = useIntl();

  return {
    label: <FormattedMessage id={`${localizationSubset}.filters.${filterKey}.label`} />,
    key: filterKey,
    renderChildren: ({ onClose }) => (
      <StaticDateRangePicker
        key={filterKey}
        shortcuts={dateRangeShortcuts.map(({ labelKey, ...el }) => ({
          ...el,
          label: intl.formatMessage({ id: labelKey }),
        }))}
        onAccept={([from, to]) => {
          if (!from && !to) {
            clearFilter(filterKey);
          } else {
            // Handle single date selection by using the same date for both from and to
            const fromDate = from || to;
            const toDate = to || from;
            
            if (!fromDate || !toDate) {
              clearFilter(filterKey);
            } else {
              setFilter({
                key: filterKey,
                value: {
                  from: fromDate,
                  to: toDate,
                  timezone,
                },
              });
            }
          }
          onClose();
        }}
        onCancel={() => {
          onClose();
          clearFilter(filterKey);
        }}
        slots={{
          actionBar: ActionBar,
        }}
      />
    ),
  };
}

function ActionBar({ onCancel, onAccept }: PickersActionBarProps) {
  return (
    <div
      // To place controls in the correct place
      style={{
        gridColumn: '1 / 4',
        gridRow: 3,
      }}
    >
      <Flex justifyContent="flex-end" px={2} py={1} minWidth="320px">
        <Button
          onClick={() => {
            onCancel();
          }}
          variant="passive"
        >
          <FormattedMessage id="common.clear" />
        </Button>
        <Button onClick={onAccept}>
          <FormattedMessage id="common.applyFilters" />
        </Button>
      </Flex>
    </div>
  );
}
